import { User } from '../data/user';

export type MembershipTier = 'free' | 'starter' | 'pro';

export type FeatureName =
  | 'general_practice_questions'
  | 'general_resources'
  | 'summary_feedback'
  | 'upload_resume'
  | 'common_questions'
  | 'behavioral_questions'
  | 'hypothetical_questions'
  | 'complete_feedback'
  | 'ideal_answer'
  | 'advanced_analytics'
  | 'interview_academy'
  | 'custom_interview'
  | 'quick_start';

// Feature access matrix based on the specification
export const FEATURE_ACCESS_MATRIX: Record<FeatureName, MembershipTier[]> = {
  // Free tier features
  general_practice_questions: ['free', 'starter', 'pro'],
  general_resources: ['free', 'starter', 'pro'],
  summary_feedback: ['free', 'starter', 'pro'],
  quick_start: ['free', 'starter', 'pro'], // Updated: Quick Start now available for all users

  // Starter tier features
  upload_resume: ['starter', 'pro'],
  common_questions: ['starter', 'pro'],
  behavioral_questions: ['starter', 'pro'],
  hypothetical_questions: ['starter', 'pro'],
  complete_feedback: ['starter', 'pro'],
  custom_interview: ['starter', 'pro'],

  // Pro tier features
  ideal_answer: ['pro'],
  advanced_analytics: ['pro'],
  interview_academy: ['pro'],
};

/**
 * Check if a user has access to a specific feature
 */
export function hasFeatureAccess(
  user: User | null | undefined,
  feature: FeatureName,
  trialRemainingDays: number = 0
): boolean {
  if (!user) return false;

  // B2B users get pro access
  if (user.client?.name) {
    return FEATURE_ACCESS_MATRIX[feature].includes('pro');
  }

  // Users in trial period get pro access
  if (user.membershipTier === 'free' && trialRemainingDays > 0) {
    return FEATURE_ACCESS_MATRIX[feature].includes('pro');
  }

  // Check normal tier access
  return FEATURE_ACCESS_MATRIX[feature].includes(user.membershipTier);
}

/**
 * Get the user's effective membership tier (accounting for trial and B2B)
 */
export function getEffectiveMembershipTier(
  user: User | null | undefined,
  trialRemainingDays: number = 0
): MembershipTier {
  if (!user) return 'free';

  // B2B users get pro tier
  if (user.client?.name) return 'pro';

  // Users in trial period get pro tier
  if (user.membershipTier === 'free' && trialRemainingDays > 0) {
    return 'pro';
  }

  return user.membershipTier;
}

/**
 * Check if a user needs to upgrade to access a feature
 */
export function needsUpgradeForFeature(
  user: User | null | undefined,
  feature: FeatureName,
  trialRemainingDays: number = 0
): boolean {
  return !hasFeatureAccess(user, feature, trialRemainingDays);
}

/**
 * Get the minimum required tier for a feature
 */
export function getMinimumRequiredTier(feature: FeatureName): MembershipTier {
  const tiers = FEATURE_ACCESS_MATRIX[feature];
  const tierOrder: MembershipTier[] = ['free', 'starter', 'pro'];

  for (const tier of tierOrder) {
    if (tiers.includes(tier)) {
      return tier;
    }
  }

  return 'pro'; // Default to pro if not found
}

/**
 * Get features available for a specific tier
 */
export function getFeaturesForTier(tier: MembershipTier): FeatureName[] {
  return Object.entries(FEATURE_ACCESS_MATRIX)
    .filter(([, tiers]) => tiers.includes(tier))
    .map(([feature]) => feature as FeatureName);
}

/**
 * Check if a user should see a premium lock icon
 */
export function shouldShowPremiumLock(
  user: User | null | undefined,
  feature: FeatureName,
  trialRemainingDays: number = 0
): boolean {
  return needsUpgradeForFeature(user, feature, trialRemainingDays);
}

/**
 * Get upgrade message for a specific feature
 */
export function getUpgradeMessage(feature: FeatureName): string {
  const minTier = getMinimumRequiredTier(feature);
  const tierNames = {
    free: 'Free',
    starter: 'Starter',
    pro: 'Pro',
  };

  return `This feature requires a ${tierNames[minTier]} subscription or higher. Upgrade to unlock this feature!`;
}

/**
 * Legacy compatibility function - maps to new feature access system
 */
export function shouldShowPremiumLockLegacy(
  isFree: boolean,
  hasFullTrialAccess: boolean,
  requiresPremium?: boolean
): boolean {
  return Boolean(requiresPremium) && isFree && !hasFullTrialAccess;
}
